// "Show QR" section (§6.1 / §6.2).
//
// Staff picks a campaign; we GET /guest/venue/token?restaurant=&campaign= and
// render the returned `venueToken` as a QR. The token rotates: the backend tells
// us `refreshInSec` (the campaign's QrRotationSeconds), and we auto-refetch a
// fresh token just before it lapses so the on-screen QR is always live.
//
// A5: the customer SCANNING this QR never mints a stamp by itself — the scan only
// LINKS their wallet to a staff-rung order (earn on completion) or, for cash
// sales, staff use the "Cash stamp" tab. This QR is pure identity/linking.
//
// The restaurant id comes from the signed staff session (req.decoded.restaurant
// on the server); the client passes it only to scope the read — staff identity is
// always re-derived from the Bearer token server-side (A4).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API, authHeaders, describeError } from './api';
import CampaignPicker from './CampaignPicker';
import QrImage from './QrImage';

const DEFAULT_ROTATION = 30;

export default function ShowQr({ restaurantId, campaigns, onAuthExpired }) {
  const [campaign, setCampaign] = useState('');
  const [token, setToken] = useState('');
  const [rotation, setRotation] = useState(DEFAULT_ROTATION);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const timerRef = useRef(null);
  const tickRef = useRef(null);
  // Guards against a stale in-flight request applying after the staff switched
  // campaigns (or unmounted).
  const reqId = useRef(0);

  const clearTimers = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    timerRef.current = null;
    tickRef.current = null;
  };

  const fetchToken = useCallback(
    async (campaignId) => {
      if (!campaignId) return;
      const myReq = ++reqId.current;
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`${API}/guest/venue/token`, {
          params: { restaurant: restaurantId, campaign: campaignId },
          headers: authHeaders(),
        });
        if (myReq !== reqId.current) return; // superseded
        const data = res.data || {};
        const vt = data.venueToken || data.token || '';
        const refresh =
          Number(data.refreshInSec) > 0
            ? Number(data.refreshInSec)
            : Number(data.QrRotationSeconds) > 0
            ? Number(data.QrRotationSeconds)
            : DEFAULT_ROTATION;
        if (!vt) {
          setToken('');
          setError('No token returned for this campaign.');
          return;
        }
        setToken(vt);
        setRotation(refresh);
        setRemaining(refresh);
      } catch (err) {
        if (myReq !== reqId.current) return;
        if (err && err.response && (err.response.status === 401 || err.response.status === 403)) {
          if (onAuthExpired) onAuthExpired();
          return;
        }
        setToken('');
        setError(describeError(err));
      } finally {
        if (myReq === reqId.current) setLoading(false);
      }
    },
    [restaurantId, onAuthExpired],
  );

  // (Re)start the rotation loop whenever we have a token + campaign.
  useEffect(() => {
    clearTimers();
    if (!campaign || !token) return undefined;

    // schedule the next fetch just as the window lapses
    timerRef.current = setTimeout(() => {
      fetchToken(campaign);
    }, Math.max(1, rotation) * 1000);

    // visible countdown
    setRemaining(rotation);
    tickRef.current = setInterval(() => {
      setRemaining((r) => (r > 0 ? r - 1 : 0));
    }, 1000);

    return clearTimers;
    // token changes each rotation -> loop re-arms
  }, [campaign, token, rotation, fetchToken]);

  // Refetch immediately when the staff picks a different campaign.
  const onPick = (id) => {
    setCampaign(id);
    setToken('');
    setError('');
    clearTimers();
    if (id) fetchToken(id);
  };

  // Pause/resume on tab visibility so a backgrounded phone doesn't keep an
  // expired QR on screen — refresh on return.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && campaign) {
        fetchToken(campaign);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [campaign, fetchToken]);

  useEffect(() => clearTimers, []);

  const pct = rotation > 0 ? Math.max(0, Math.min(100, (remaining / rotation) * 100)) : 0;

  return (
    <div className="staff-card">
      <h2>Show QR</h2>
      <p className="staff-hint">
        Customer scans this to start or add to their card. It refreshes
        automatically — keep it on screen at the counter.
      </p>

      <CampaignPicker
        id="qr-campaign"
        campaigns={campaigns}
        value={campaign}
        onChange={onPick}
      />

      {error ? <div className="staff-alert is-error">{error}</div> : null}

      {campaign ? (
        <div className="staff-qr-wrap">
          <div className="staff-qr-box">
            {/* Encode the full /q/<token> URL (§6.2): a phone-camera scan must
                OPEN the QR landing page — a bare venue token is just an
                unactionable string to the camera app. */}
            {loading && !token ? (
              <div className="staff-qr-fallback">Loading…</div>
            ) : (
              <QrImage value={`${window.location.origin}/q/${token}`} />
            )}
          </div>
          {token ? (
            <>
              <div className="staff-qr-progress">
                <div style={{ width: `${pct}%` }} />
              </div>
              <div className="staff-qr-meta">
                Refreshes in <span className="staff-qr-countdown">{remaining}s</span>
                {loading ? ' · updating…' : ''}
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <p className="staff-hint" style={{ marginBottom: 0 }}>
          Pick a campaign to display its QR.
        </p>
      )}
    </div>
  );
}
